import { showTopNotification } from "./notification.js";
import { getFrontendToolbar } from "../utils/dom.js";
import DockableWindow from "./dockable-window.js";
import { resolveAvatarSrc } from "../../avatars/index.js";

const SVG_ATTRS = `viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"`;

const ICONS = {
    headset: `<svg ${SVG_ATTRS}><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>`,
    mic: `<svg ${SVG_ATTRS}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`,
    micOff: `<svg ${SVG_ATTRS}><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`,
    users: `<svg ${SVG_ATTRS}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
    video: `<svg ${SVG_ATTRS}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`,
    videoOff: `<svg ${SVG_ATTRS}><path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`,
    lock: `<svg ${SVG_ATTRS}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`,
    hangup: `<svg ${SVG_ATTRS}><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/><line x1="23" y1="1" x2="1" y2="23"/></svg>`,
};

const MESSAGES = {
    "mic-denied":
        "No se pudo acceder al micrófono. Puedes seguir escuchando la llamada.",
    "camera-denied": "No se pudo acceder a la cámara.",
    "cameras-full":
        "Ya hay demasiadas cámaras encendidas. Inténtalo más tarde.",
    "muted-by-teacher": "El profesor ha silenciado a todos.",
    "mic-locked": "El profesor ha bloqueado los micrófonos.",
    "mic-unlocked": "El profesor ha desbloqueado los micrófonos.",
    "camera-locked": "El profesor ha bloqueado las cámaras.",
    "camera-unlocked": "El profesor ha desbloqueado las cámaras.",
    full: "La llamada de voz está completa.",
    disconnected:
        "Se perdió la conexión con el servidor y has salido de la llamada de voz.",
    default: "Ha ocurrido un error en la llamada de voz.",
};

const CONNECTION_LABELS = {
    new: "Conectando…",
    connecting: "Conectando…",
    disconnected: "Reconectando…",
    failed: "Sin conexión",
};

const SELF_TILE = "self";

const MIN_RESIZED_TILE = 100;
const TILE_ASPECT = 4 / 3;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const CSS = `
    .ls-voice {
        position: relative;
        order: 2;
        font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    }

    .ls-voice [hidden] {
        display: none !important;
    }

    .ls-voice-bar {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px;
        background: #fff;
        border: 1px solid rgba(133, 92, 214, 0.25);
        border-radius: 999px;
        box-shadow: 0 2px 8px rgba(133, 92, 214, 0.25);
    }

    .ls-voice-bar.is-idle {
        padding: 0;
        border: none;
        background: none;
        box-shadow: none;
    }

    .ls-voice-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        height: 32px;
        min-width: 32px;
        padding: 0 8px;
        border: none;
        border-radius: 999px;
        background: #f3eefc;
        color: #855cd6;
        font: inherit;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: transform 0.15s ease, filter 0.15s ease,
            box-shadow 0.15s ease, background 0.15s ease;
    }

    .ls-voice-btn svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }

    .ls-voice-btn:hover:not(:disabled) {
        filter: brightness(1.05);
        transform: scale(1.06);
    }

    .ls-voice-btn:active:not(:disabled) {
        transform: scale(1);
    }

    .ls-voice-btn:disabled {
        opacity: 0.6;
        cursor: progress;
    }

    .ls-voice-join {
        width: 36px;
        height: 36px;
        padding: 0;
        color: #fff;
        background: linear-gradient(135deg, #855cd6 0%, #6b4bb5 100%);
        box-shadow: 0 2px 8px rgba(133, 92, 214, 0.35);
    }

    .ls-voice-join svg {
        width: 18px;
        height: 18px;
    }

    .ls-voice-mic,
    .ls-voice-cam,
    .ls-voice-leave {
        width: 32px;
        padding: 0;
    }

    .ls-voice-mic.is-live,
    .ls-voice-cam.is-live {
        background: #0fbd8c;
        color: #fff;
    }

    .ls-voice-btn.is-locked {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .ls-voice-mic.is-speaking {
        box-shadow: 0 0 0 3px rgba(15, 189, 140, 0.35);
    }

    .ls-voice-people.is-active {
        background: #855cd6;
        color: #fff;
    }

    .ls-voice-leave {
        background: #ff6680;
        color: #fff;
    }

    .ls-voice-panel-body {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    }

    .ls-voice-hint-row {
        padding: 10px 14px;
        font-size: 12px;
        color: #7a6f99;
        border-bottom: 1px solid #f0f0f5;
        flex-shrink: 0;
    }

    .ls-voice-list {
        list-style: none;
        margin: 0;
        padding: 6px;
        max-height: 170px;
        overflow-y: auto;
        flex-shrink: 0;
        border-bottom: 1px solid #f0f0f5;
    }

    .ls-voice-person {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 6px 8px;
        border-radius: 8px;
    }

    .ls-voice-person:hover {
        background: #f8f6fc;
    }

    .ls-voice-avatar {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 700;
        color: #4a3f6b;
        background: #fff;
        border: 2.5px solid var(--user-color);
        transition: box-shadow 0.15s ease;
    }

    .ls-voice-avatar img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
        display: block;
    }

    .ls-voice-person.is-speaking .ls-voice-avatar {
        box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0fbd8c;
    }

    .ls-voice-info {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        min-width: 0;
    }

    .ls-voice-name {
        font-size: 12px;
        font-weight: 600;
        color: #4a3f6b;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .ls-voice-sub {
        font-size: 10px;
        font-weight: 700;
        color: #ffa500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .ls-voice-sub.is-warning {
        color: #a89bc7;
        text-transform: none;
        letter-spacing: 0;
    }

    .ls-voice-person-mic {
        display: flex;
        color: #0fbd8c;
    }

    .ls-voice-person-mic.is-muted {
        color: #c1b6dc;
    }

    .ls-voice-person-mic svg {
        width: 14px;
        height: 14px;
    }

    .ls-voice-person-cam {
        display: flex;
        color: #855cd6;
    }

    .ls-voice-person-cam svg {
        width: 14px;
        height: 14px;
    }

    .ls-voice-mod {
        padding: 10px 14px;
        border-bottom: 1px solid #f0f0f5;
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex-shrink: 0;
    }

    .ls-voice-mod-title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 700;
        color: #ffa500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .ls-voice-mod-title svg {
        width: 12px;
        height: 12px;
    }

    .ls-voice-mute-all {
        padding: 7px;
        border: none;
        border-radius: 8px;
        background: #ff6680;
        color: #fff;
        font: inherit;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
    }

    .ls-voice-toggle {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: #4a3f6b;
        cursor: pointer;
    }

    .ls-voice-toggle input {
        accent-color: #855cd6;
        margin: 0;
        cursor: pointer;
    }

    .ls-voice-camera-section {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    .ls-voice-camera-label {
        padding: 8px 14px 4px;
        font-size: 11px;
        font-weight: 700;
        color: #855cd6;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        flex-shrink: 0;
    }

    .ls-voice-grid-wrap {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        /* A scrollbar popping in would narrow the grid, re-layout the tiles
           and possibly remove itself again, flickering back and forth. */
        scrollbar-gutter: stable;
        padding: 6px 10px 10px;
    }
    .ls-voice-grid-wrap::-webkit-scrollbar { width: 6px; height: 6px; }
    .ls-voice-grid-wrap::-webkit-scrollbar-thumb { background: rgba(133, 92, 214, 0.25); border-radius: 999px; }

    /* Tile widths are set by layoutTiles(): the ones the user resized keep
       their share of the row and the rest split what is left, from the
       min to the max below. */
    .ls-voice-grid {
        --ls-voice-tile-min: 150px;
        --ls-voice-tile-max: 320px;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        align-content: flex-start;
        gap: 6px;
    }

    .ls-voice-tile {
        position: relative;
        flex: none;
        width: var(--ls-voice-tile-min);
        max-width: 100%;
        aspect-ratio: 4 / 3;
        border-radius: 10px;
        overflow: hidden;
        background: #14101f;
        border: 2px solid transparent;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        transition: border-color 0.15s ease;
        box-sizing: border-box;
    }

    .ls-voice-tile.is-speaking {
        border-color: #0fbd8c;
    }

    .ls-voice-tile video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }

    .ls-voice-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: 20px;
        text-align: center;
        font-size: 13px;
        color: #a89bc7;
    }

    .ls-voice-tile.is-self video {
        transform: scaleX(-1);
    }

    .ls-voice-tile-grip {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 18px;
        height: 18px;
        cursor: nwse-resize;
        touch-action: none;
        opacity: 0;
        transition: opacity 0.15s ease;
        background: linear-gradient(
            135deg,
            transparent 55%,
            rgba(255, 255, 255, 0.75) 55%
        );
    }

    .ls-voice-tile:hover .ls-voice-tile-grip,
    .ls-voice-tile.is-resizing .ls-voice-tile-grip {
        opacity: 1;
    }

    .ls-voice-grid.is-resizing {
        user-select: none;
    }

    .ls-voice-tile-name {
        position: absolute;
        left: 6px;
        bottom: 6px;
        max-width: calc(100% - 30px);
        padding: 2px 6px;
        border-radius: 6px;
        background: rgba(0, 0, 0, 0.55);
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        box-sizing: border-box;
    }

    .ls-dock.is-anchored .ls-voice-hint-row,
    .ls-dock.is-anchored .ls-voice-mod,
    .ls-dock.is-anchored .ls-voice-list,
    .ls-dock.is-anchored .ls-voice-camera-label {
        display: none;
    }
    .ls-dock.is-anchored .ls-voice-grid-wrap {
        overflow-x: hidden;
        overflow-y: auto;
        padding: 6px;
    }
    .ls-dock.is-anchored .ls-voice-grid {
        --ls-voice-tile-max: 190px;
    }

    .ls-dock.is-expanded .ls-voice-hint-row,
    .ls-dock.is-expanded .ls-voice-mod,
    .ls-dock.is-expanded .ls-voice-list,
    .ls-dock.is-expanded .ls-voice-camera-label {
        display: none;
    }
    .ls-dock.is-expanded .ls-voice-grid-wrap {
        display: flex;
        padding: 24px;
    }
    .ls-dock.is-expanded .ls-voice-grid {
        /* auto margins center the grid without clipping it once it
           overflows and the wrapper has to scroll. */
        margin: auto;
        width: 100%;
        max-width: 1500px;
        justify-content: center;
        gap: 16px;
        --ls-voice-tile-min: 300px;
        --ls-voice-tile-max: 640px;
    }
    .ls-dock.is-expanded .ls-voice-tile {
        border-radius: 14px;
        background: rgba(0, 0, 0, 0.45);
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
    }
    .ls-dock.is-expanded .ls-voice-tile-name {
        font-size: 13px;
    }
    .ls-dock.is-expanded .ls-voice-empty {
        color: rgba(255, 255, 255, 0.75);
        font-size: 15px;
    }
`;

export default class VoicePanel {
    constructor(voiceCall, { username, color, avatar, isTeacher }) {
        this.call = voiceCall;
        this.self = { username, color, avatar, isTeacher };

        this.root = null;
        this.dock = null;
        this.els = {};
        this.tiles = new Map(); // Map<socketID | "self", Tile>
        this.tileSizes = new Map(); // Map<socketID | "self", share of the row width>
        this.gridObserver = null;
        this.unsubscribe = null;

        this.injectCSS();
        if (!this.mount()) return;

        this.unsubscribe = this.call.subscribe((event) => {
            if (event.type === "error" || event.type === "notice") {
                showTopNotification(MESSAGES[event.reason] || MESSAGES.default);
            }
            this.render();
        });
        this.render();
    }

    injectCSS() {
        if (document.getElementById("ls-voice-style")) return;

        const style = document.createElement("style");
        style.id = "ls-voice-style";
        style.textContent = CSS;
        document.head.appendChild(style);
    }

    mount() {
        const toolbar = getFrontendToolbar();
        if (!toolbar) {
            console.error("VoicePanel: editor toolbar not found");
            return false;
        }

        this.root = document.createElement("div");
        this.root.id = "ls-voice";
        this.root.className = "ls-voice";
        this.root.innerHTML = `
            <div class="ls-voice-bar">
                <button type="button" class="ls-voice-btn ls-voice-join" data-action="join" title="Unirse a la llamada">${ICONS.headset}</button>
                <button type="button" class="ls-voice-btn ls-voice-mic" data-action="mute"></button>
                <button type="button" class="ls-voice-btn ls-voice-cam" data-action="camera"></button>
                <button type="button" class="ls-voice-btn ls-voice-people" data-action="people" title="Participantes y cámaras">${ICONS.users}<span class="ls-voice-count"></span></button>
                <button type="button" class="ls-voice-btn ls-voice-leave" data-action="leave" title="Salir de la llamada">${ICONS.hangup}</button>
            </div>
        `;

        const query = (selector) => this.root.querySelector(selector);
        this.els = {
            bar: query(".ls-voice-bar"),
            join: query(".ls-voice-join"),
            mic: query(".ls-voice-mic"),
            cam: query(".ls-voice-cam"),
            people: query(".ls-voice-people"),
            count: query(".ls-voice-count"),
            leave: query(".ls-voice-leave"),
        };

        this.root.addEventListener("click", (e) => this.onBarClick(e));
        toolbar.appendChild(this.root);

        this.dock = new DockableWindow({
            id: "ls-voice-dock",
            title: "Videollamada",
            width: 380,
            height: 520,
            minWidth: 300,
            minHeight: 320,
            maxWidth: 640,
            maxHeight: "85vh",
            expandable: true,
            onClose: () => this.render(),
            onExpandChange: () => this.render(),
            onAnchorChange: () => this.render(),
        });
        this.dock.bodyEl.innerHTML = `
            <div class="ls-voice-panel-body">
                <div class="ls-voice-hint-row"></div>
                <div class="ls-voice-mod" hidden>
                    <div class="ls-voice-mod-title">${ICONS.lock}Controles del profesor</div>
                    <button type="button" class="ls-voice-mute-all" data-action="mute-all">Silenciar a todos</button>
                    <label class="ls-voice-toggle">
                        <input type="checkbox" data-action="lock-mic">
                        Impedir activar el micrófono
                    </label>
                    <label class="ls-voice-toggle">
                        <input type="checkbox" data-action="lock-camera">
                        Impedir encender la cámara
                    </label>
                </div>
                <ul class="ls-voice-list"></ul>
                <div class="ls-voice-camera-section" hidden>
                    <div class="ls-voice-camera-label"></div>
                    <div class="ls-voice-grid-wrap">
                        <div class="ls-voice-grid"></div>
                        <div class="ls-voice-empty" hidden>Nadie tiene la cámara encendida.</div>
                    </div>
                </div>
            </div>
        `;
        this.dock.bodyEl.addEventListener("click", (e) => this.onDockClick(e));
        this.dock.bodyEl.addEventListener("change", (e) => this.onDockChange(e));

        const dockQuery = (selector) => this.dock.bodyEl.querySelector(selector);
        Object.assign(this.els, {
            hint: dockQuery(".ls-voice-hint-row"),
            mod: dockQuery(".ls-voice-mod"),
            lockMic: dockQuery('[data-action="lock-mic"]'),
            lockCamera: dockQuery('[data-action="lock-camera"]'),
            list: dockQuery(".ls-voice-list"),
            cameraSection: dockQuery(".ls-voice-camera-section"),
            cameraLabel: dockQuery(".ls-voice-camera-label"),
            grid: dockQuery(".ls-voice-grid"),
            empty: dockQuery(".ls-voice-empty"),
        });

        // Floating, docked and expanded views all change the grid's width.
        // Its height follows the layout itself, so that must not retrigger it,
        // and the layout waits for the next frame: resizing the tiles inside
        // the observer callback would notify it again in the same frame.
        let gridWidth = 0;
        let layoutFrame = null;
        this.gridObserver = new ResizeObserver(([entry]) => {
            if (entry.contentRect.width === gridWidth) return;
            gridWidth = entry.contentRect.width;

            if (layoutFrame) return;
            layoutFrame = requestAnimationFrame(() => {
                layoutFrame = null;
                this.layoutTiles();
            });
        });
        this.gridObserver.observe(this.els.grid);

        return true;
    }

    onBarClick(e) {
        const target = e.target.closest("[data-action]");

        switch (target?.dataset.action) {
            case "join":
                this.call.join();
                this.openDock();
                break;
            case "mute":
                this.call.toggleMute();
                break;
            case "camera":
                this.call.toggleCamera();
                break;
            case "people":
                if (this.dock.isOpen()) this.dock.close();
                else this.openDock();
                break;
            case "leave":
                this.call.leave();
                break;
        }
    }

    onDockClick(e) {
        const target = e.target.closest("[data-action]");

        switch (target?.dataset.action) {
            case "mute-all":
                this.call.muteAll();
                break;
        }
    }

    // Browsers refuse to play incoming audio until the page has seen a user
    // gesture. Rather than asking for one with a button nobody understands,
    // wait for the next click/keypress anywhere and unblock it silently.
    unblockAudioOnNextGesture() {
        if (this.waitingForGesture) return;
        this.waitingForGesture = true;

        const unblock = () => {
            this.waitingForGesture = false;
            document.removeEventListener("pointerdown", unblock, true);
            document.removeEventListener("keydown", unblock, true);
            this.call.resumeAudio();
        };

        document.addEventListener("pointerdown", unblock, true);
        document.addEventListener("keydown", unblock, true);
    }

    onDockChange(e) {
        const target = e.target.closest("[data-action]");

        switch (target?.dataset.action) {
            case "lock-mic":
                this.call.setPolicy({ micLocked: target.checked });
                break;
            case "lock-camera":
                this.call.setPolicy({ cameraLocked: target.checked });
                break;
        }
    }

    openDock() {
        this.dock.positionNear(this.els.people);
        this.dock.open();
        this.render();
    }

    render() {
        const state = this.call.getState();
        const inCall = state.status !== "idle";
        const { bar, join, mic, cam, people, count, leave } = this.els;

        bar.classList.toggle("is-idle", !inCall);
        join.hidden = inCall;
        mic.hidden = !inCall;
        cam.hidden = !inCall;
        people.hidden = !inCall;
        leave.hidden = !inCall;

        mic.disabled = state.status !== "joined" || state.requestingMic;
        mic.classList.toggle("is-live", !state.muted);
        mic.classList.toggle("is-speaking", state.selfSpeaking);
        mic.classList.toggle("is-locked", state.micLocked && state.muted);
        if (state.requestingMic) mic.title = "Esperando permiso del micrófono…";
        else if (state.micLocked && state.muted)
            mic.title = "El profesor ha bloqueado los micrófonos";
        else
            mic.title = state.muted
                ? "Activar micrófono"
                : "Silenciar micrófono";
        this.setIcon(mic, state.muted ? "micOff" : "mic");

        cam.disabled = state.status !== "joined" || state.requestingCamera;
        cam.classList.toggle("is-live", state.camera);
        cam.classList.toggle("is-locked", state.cameraLocked && !state.camera);
        if (state.requestingCamera)
            cam.title = "Esperando permiso de la cámara…";
        else if (state.cameraLocked && !state.camera)
            cam.title = "El profesor ha bloqueado las cámaras";
        else cam.title = state.camera ? "Apagar cámara" : "Encender cámara";
        this.setIcon(cam, state.camera ? "video" : "videoOff");

        count.textContent =
            state.status === "joined" ? state.participants.length + 1 : "…";

        if (!inCall && this.dock.isOpen()) {
            this.dock.close();
            return;
        }
        if (state.audioBlocked) this.unblockAudioOnNextGesture();

        people.classList.toggle("is-active", this.dock.isOpen());
        if (this.dock.isOpen()) this.renderPanel(state);

        this.renderCameras(state);
    }

    setIcon(button, icon) {
        if (button.dataset.icon === icon) return;
        button.innerHTML = ICONS[icon];
        button.dataset.icon = icon;
    }

    renderPanel(state) {
        const { list, hint, mod, lockMic, lockCamera } = this.els;

        list.replaceChildren(
            this.createPersonItem({
                ...this.self,
                isSelf: true,
                muted: state.muted,
                camera: state.camera,
                speaking: state.selfSpeaking,
                connection: "connected",
            }),
            ...state.participants.map((p) => this.createPersonItem(p)),
        );

        // The mic/camera buttons already show their own state, so the row is
        // only worth its space for something they can't convey.
        if (state.status === "joining")
            hint.textContent = "Uniéndote a la llamada…";
        else if (state.micLocked && state.cameraLocked)
            hint.textContent =
                "El profesor ha bloqueado los micrófonos y las cámaras.";
        else if (state.micLocked)
            hint.textContent = "El profesor ha bloqueado los micrófonos.";
        else if (state.cameraLocked)
            hint.textContent = "El profesor ha bloqueado las cámaras.";
        else hint.textContent = "";

        hint.hidden = !hint.textContent;

        mod.hidden = !state.isTeacher || state.status !== "joined";
        lockMic.checked = state.policy.micLocked;
        lockCamera.checked = state.policy.cameraLocked;
    }

    createPersonItem(person) {
        const username = person.username || "Anónimo";

        const item = document.createElement("li");
        item.className = "ls-voice-person";
        item.classList.toggle("is-speaking", person.speaking);

        const avatar = document.createElement("span");
        avatar.className = "ls-voice-avatar";
        avatar.style.setProperty("--user-color", person.color || "#855cd6");

        const avatarSrc = person.avatar ? resolveAvatarSrc(person.avatar) : null;
        if (avatarSrc) {
            const image = document.createElement("img");
            image.src = avatarSrc;
            image.alt = "";
            avatar.appendChild(image);
        } else {
            avatar.textContent = username.charAt(0).toUpperCase();
        }

        const info = document.createElement("span");
        info.className = "ls-voice-info";

        const name = document.createElement("span");
        name.className = "ls-voice-name";
        name.textContent = person.isSelf ? `${username} (tú)` : username;
        info.appendChild(name);

        const connectionLabel = CONNECTION_LABELS[person.connection];
        if (connectionLabel || person.isTeacher) {
            const sub = document.createElement("span");
            sub.className = "ls-voice-sub";
            sub.classList.toggle("is-warning", !!connectionLabel);
            sub.textContent = connectionLabel || "Profesor";
            info.appendChild(sub);
        }

        item.append(avatar, info);

        if (person.camera) {
            const camState = document.createElement("span");
            camState.className = "ls-voice-person-cam";
            camState.title = "Cámara encendida";
            camState.innerHTML = ICONS.video;
            item.appendChild(camState);
        }

        const micState = document.createElement("span");
        micState.className = "ls-voice-person-mic";
        micState.classList.toggle("is-muted", person.muted);
        micState.title = person.muted ? "Silenciado" : "Micrófono activo";
        micState.innerHTML = person.muted ? ICONS.micOff : ICONS.mic;
        item.appendChild(micState);

        return item;
    }

    renderCameras(state) {
        const feeds = [];

        const localStream = this.call.getLocalVideoStream();
        if (state.camera && localStream) {
            feeds.push({
                id: SELF_TILE,
                stream: localStream,
                username: this.self.username,
                isSelf: true,
                speaking: state.selfSpeaking,
            });
        }

        state.participants.forEach((p) => {
            const stream = this.call.getVideoStream(p.id);
            if (p.camera && stream) feeds.push({ ...p, stream });
        });

        const visibleIds = new Set(feeds.map((feed) => feed.id));
        this.tiles.forEach((tile, id) => {
            if (visibleIds.has(id)) return;
            tile.video.srcObject = null;
            tile.item.remove();
            this.tiles.delete(id);
            this.tileSizes.delete(id);
        });

        feeds.forEach((feed) => this.updateTile(feed));
        this.layoutTiles();

        const { cameraSection, cameraLabel, grid, empty } = this.els;

        // Expanded is a cameras-only view, so it keeps the section on screen
        // and explains itself when there is nothing to show.
        const expanded = this.dock.isExpanded();
        cameraSection.hidden = feeds.length === 0 && !expanded;
        grid.hidden = feeds.length === 0;
        empty.hidden = feeds.length > 0;
        cameraLabel.textContent = `Cámaras (${feeds.length})`;

        // With no cameras there is nothing that needs the extra room, so the
        // window collapses to its content instead of showing a blank half.
        this.dock.setAutoHeight(feeds.length === 0);
    }

    updateTile(feed) {
        let tile = this.tiles.get(feed.id);

        if (!tile) {
            const item = document.createElement("div");
            item.className = "ls-voice-tile";
            item.classList.toggle("is-self", !!feed.isSelf);

            const video = document.createElement("video");
            video.autoplay = true;
            video.muted = true;
            video.playsInline = true;

            const name = document.createElement("span");
            name.className = "ls-voice-tile-name";

            const grip = document.createElement("div");
            grip.className = "ls-voice-tile-grip";
            grip.title =
                "Arrastra para cambiar el tamaño (doble clic para restablecerlo)";
            this.enableTileResize(feed.id, item, grip);

            item.dataset.tileId = feed.id;
            item.append(video, name, grip);
            if (feed.isSelf) this.els.grid.prepend(item);
            else this.els.grid.appendChild(item);

            tile = { item, video, name };
            this.tiles.set(feed.id, tile);
        }

        if (tile.video.srcObject !== feed.stream) {
            tile.video.srcObject = feed.stream;
            tile.video.play().catch(() => {});
        }

        const username = feed.username || "Anónimo";
        tile.name.textContent = feed.isSelf ? `${username} (tú)` : username;
        tile.item.classList.toggle("is-speaking", !!feed.speaking);
    }

    enableTileResize(id, item, grip) {
        let startX = 0;
        let startY = 0;
        let startWidth = 0;

        const onMove = (e) => {
            const dx = e.clientX - startX;
            const dy = (e.clientY - startY) * TILE_ASPECT;
            // Follows whichever direction the pointer moved the most
            const growth = Math.abs(dx) >= Math.abs(dy) ? dx : dy;

            const rowWidth = this.els.grid.clientWidth;
            if (!rowWidth) return;

            const width = clamp(startWidth + growth, MIN_RESIZED_TILE, rowWidth);
            this.tileSizes.set(id, width / rowWidth);
            this.layoutTiles();
        };

        const onEnd = (e) => {
            grip.releasePointerCapture?.(e.pointerId);
            grip.removeEventListener("pointermove", onMove);
            grip.removeEventListener("pointerup", onEnd);
            grip.removeEventListener("pointercancel", onEnd);
            item.classList.remove("is-resizing");
            this.els.grid.classList.remove("is-resizing");
        };

        grip.addEventListener("pointerdown", (e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();

            startX = e.clientX;
            startY = e.clientY;
            startWidth = item.getBoundingClientRect().width;

            grip.setPointerCapture?.(e.pointerId);
            grip.addEventListener("pointermove", onMove);
            grip.addEventListener("pointerup", onEnd);
            grip.addEventListener("pointercancel", onEnd);
            item.classList.add("is-resizing");
            this.els.grid.classList.add("is-resizing");
        });

        grip.addEventListener("dblclick", (e) => {
            e.stopPropagation();
            this.tileSizes.delete(id);
            this.layoutTiles();
        });
    }

    // Resized tiles go first and keep their share of the row; the others
    // fill the room left beside them and then wrap below, all of the same
    // width within the min/max of the current view.
    layoutTiles() {
        const { grid } = this.els;
        const rowWidth = grid?.clientWidth;
        if (!rowWidth) return;

        const style = getComputedStyle(grid);
        const gap = parseFloat(style.columnGap) || 0;
        const max = parseFloat(style.getPropertyValue("--ls-voice-tile-max"));
        const min = Math.min(
            rowWidth,
            parseFloat(style.getPropertyValue("--ls-voice-tile-min")),
        );

        const items = Array.from(grid.children);
        const resized = items.filter((item) =>
            this.tileSizes.has(item.dataset.tileId),
        );
        let others = items.filter(
            (item) => !this.tileSizes.has(item.dataset.tileId),
        );

        // Room taken on the line where the resized tiles end
        let lineUsed = 0;
        resized.forEach((item) => {
            const share = this.tileSizes.get(item.dataset.tileId);
            const width = clamp(share * rowWidth, MIN_RESIZED_TILE, rowWidth);
            if (lineUsed > 0 && lineUsed + width > rowWidth) lineUsed = 0;
            lineUsed += width + gap;

            item.style.order = "0";
            item.style.width = `${width}px`;
        });

        const fit = (space, count) => {
            const columns = clamp(
                Math.floor((space + gap) / (min + gap)),
                1,
                count,
            );
            const width = Math.min(max, (space - gap * (columns - 1)) / columns);
            return { columns, width };
        };
        const setWidth = (tiles, width) =>
            tiles.forEach((item) => {
                item.style.order = "1";
                item.style.width = `${width}px`;
            });

        const besideResized = rowWidth - lineUsed;
        if (resized.length && others.length && besideResized >= min) {
            const { columns, width } = fit(besideResized, others.length);
            setWidth(others.slice(0, columns), width);
            others = others.slice(columns);
        }

        if (others.length) setWidth(others, fit(rowWidth, others.length).width);
    }

    destroy() {
        this.gridObserver?.disconnect();
        this.unsubscribe?.();
        this.tiles.forEach((tile) => {
            tile.video.srcObject = null;
        });
        this.tiles.clear();
        this.dock?.destroy();
        this.root?.remove();
    }
}
